import { For, Show, type JSX } from "solid-js";
import styles from "./Tabs.module.scss";

export type Tab = {
  id: string;
  label: string;
  /** Built when the tab is shown, so a panel nobody opens costs nothing. */
  content: () => JSX.Element;
};

export type TabsProps = {
  tabs: readonly Tab[];
  /** The open tab. An id that names no tab falls back to the first one. */
  active: string;
  onPick: (id: string) => void;
  /** Names the strip, since the tabs themselves only carry their own labels. */
  label: string;
};

/**
 * One strip of tabs over one panel, for a screen that holds more sections than
 * a reader can scroll past. Only the open panel is mounted: the sections here
 * carry tables wide enough that keeping all of them alive costs real work.
 */
export const Tabs = (props: TabsProps) => {
  const open = () => props.tabs.find((tab) => tab.id === props.active) ?? props.tabs[0];

  /** Arrow keys walk the strip, which is what a tablist is expected to do. */
  const onKey = (event: KeyboardEvent, index: number) => {
    const step =
      event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : undefined;
    const target =
      event.key === "Home"
        ? 0
        : event.key === "End"
          ? props.tabs.length - 1
          : step === undefined
            ? undefined
            : (index + step + props.tabs.length) % props.tabs.length;
    if (target === undefined) return;

    const tab = props.tabs[target];
    if (tab === undefined) return;
    event.preventDefault();
    props.onPick(tab.id);
    document.getElementById(`tab-${tab.id}`)?.focus();
  };

  return (
    <div class={styles.tabs}>
      <div class={styles.strip} role="tablist" aria-label={props.label}>
        <For each={props.tabs}>
          {(tab, index) => (
            <button
              id={`tab-${tab.id}`}
              class={`${styles.tab} ${open()?.id === tab.id ? styles.active : ""}`}
              type="button"
              role="tab"
              aria-selected={open()?.id === tab.id}
              aria-controls={`panel-${tab.id}`}
              /* Roving tabindex: one stop for the strip, then arrows inside it. */
              tabindex={open()?.id === tab.id ? 0 : -1}
              onClick={() => props.onPick(tab.id)}
              onKeyDown={(event) => onKey(event, index())}
            >
              {tab.label}
            </button>
          )}
        </For>
      </div>

      <Show when={open()}>
        {(tab) => (
          <div
            id={`panel-${tab().id}`}
            class={styles.panel}
            role="tabpanel"
            aria-labelledby={`tab-${tab().id}`}
            tabindex={0}
          >
            {tab().content()}
          </div>
        )}
      </Show>
    </div>
  );
};
